import {useI18Next} from "../../../../../../../common/helpers/react/hooks/useI18Next";
import {default as React, useState} from "react";
import {cssClasses} from "../../../../../../../common/helpers/browser/cssClasses";
import {Accordion, Drawer} from "@sabre/spark-react-core";
import {useControllers} from "../../../../../../../common/helpers/react/hooks/useControllers";
import {useParams} from "react-router-dom";
import {asNumber} from "../../../../../../../common/helpers/converters/asNumber";
import {useHashWizard} from "../../../../../common/components/wizards/useHashWizard";
import {
    ApplicationOptions
} from "../../../../../../../common/model/types/applicationOptions/ApplicationOptions";
import {ColumnDescription} from "../../../../../common/components/table/ColumnDescription";
import DefaultListTable from "../../../../../common/components/table/DefaultListTable";
import DefaultTableInput from "../../../../../common/components/table/inputs/DefaultTableInput";
import {getApplicationInputCellHelpers} from "./misc/getApplicationInputCellHelpers";
import {asString} from "../../../../../../../common/helpers/converters/asString";
import DefaultToggle from "../../../../../common/components/toggle/DefaultToggle";
import {
    ToggleOptionDescription
} from "../../../../../common/components/toggle/ToggleOptionDescription";
import {KEY} from "../../../../../../../common/helpers/id/KEY";

import styles from "./GroupWizardApplicationStepAccordion.module.css";
import {isArrayOf} from "../../../../../../../common/types/guards/isArrayOf";
import {isString} from "../../../../../../../common/types/guards/isString";
import {isTruthyString} from "../../../../../../../common/types/guards/isTruthyString";
import {RxBigRock} from "../../../../../../../common/model/db/types/RxBigRock";
import {DefaultWizardHelpers} from "../../../../../common/components/wizards/DefaultWizardHelpers";
import {BigRockBody} from "./BigRockBody";
import {PlatformAccess} from "../../../../../../../core/rest-client/aat";


export default function GroupWizardApplicationStepAccordion() {
    const {t} = useI18Next();
    const {id} = useParams();
    const wh = useHashWizard();

    const controllers = useControllers();
    const groups = controllers.groups;
    const bigRocks = controllers.bigRocks;

    const group = groups.getGroupById$(asNumber(id)).useState();
    const applicationOptions = [
        {
            feature: 'Application',
            type: 'application_type',
            settings: group && group.application_type
                ? group.application_type
                : '',
            description: 'Select Agency Workspace or Agency Workspace Lite',
            possible_values: new Map<string, string>([
                ['-- Select --', ''],
                ['Agency Workspace', 'dynamo'],
                ['Agency Workspace Lite', 'launchpad']
            ])
        },
        {
            feature: t('PLATFORM_ACCESS'),
            type: 'platform_access',
            onlyFor: 'dynamo',
            settings: group && group.platform_access
                ? group.platform_access
                : '',
            description: t('PLATFORM_ACCESS_DESCRIPTION'),
            possible_values: new Map<string, string>([
                [t('PLATFORM_ACCESS_SELECT'), ''],
                [t('PLATFORM_ACCESS_DESKTOP_ONLY'), PlatformAccess.DesktopOnly],
                [t('PLATFORM_ACCESS_DESKTOP_WEB'), PlatformAccess.DesktopWeb],
                [t('PLATFORM_ACCESS_WEB_ONLY'), PlatformAccess.WebOnly]
            ])
        },
        {
            feature: 'Welcome screen',
            type: 'welcome_screen',
            onlyFor: 'launchpad',
            settings: group && group.welcome_screen
                ? group.welcome_screen
                : '',
            description: 'Allows for choosing the welcome screen for when the application is launched',
            possible_values: new Map<string, string>([
                ['-- Select --', ''],
                ['Flights search', 'flights'],
                ['Hotels search', 'hotels'],
                ['Reservations dashboard', 'pnr-dashboard']
            ])
        },
        {
            feature: 'Hold reservation',
            type: 'hold_reservation',
            onlyFor: 'launchpad',
            settings: group && group.hold_reservation
                ? group.hold_reservation
                : '',
            description: 'Manages appearance of the "Hold Reservation" button in the "Reservation" screen',
            possible_values: new Map<string, string>([
                ['-- Select --', ''],
                ['Disabled', 'false'],
                ['Enabled', 'true']
            ])
        }
    ];

    const initialValue = asString(wh.stepData?.application_type ?? applicationOptions[0]?.settings ?? '');

    const [applicationType, setApplicationType] = useState(initialValue);
    const applicationDrawerTable = useApplicationDrawerTable(applicationOptions);

    const bigRocksData = bigRocks.getBigRocks().useState() ?? [];
    const bigRockRoleIdsSaved = getBigRockRoleIdsSaved(bigRocksData, id);
    const bigRockRoleIdsUnsaved = getBigRockRoleIdsUnsaved(wh, bigRockRoleIdsSaved, bigRocksData, applicationType);


    function useApplicationDrawerTable(applicationOptions?: ApplicationOptions[]) {
        const columns: ColumnDescription<ApplicationOptions>[] = [
            {
                header: t('FEATURE'),
                size: 1,
                render: row => {
                    return (
                        <>
                            <div className={styles.GroupName}>
                                {row?.feature}
                            </div>
                        </>
                    );
                }
            },
            {
                header: t('SETTINGS'),
                size: 2,
                render: row => {
                    const {
                        initialValue,
                        onChange,
                    } = getApplicationInputCellHelpers(row, wh);

                    const options: { label: string, value: string }[] = [];

                    row.possible_values
                        ?.forEach((value, label) => {
                            options.push({label, value});
                        });

                    const onChangeValue = async (event: React.ChangeEvent<HTMLElement>, value: string) => {
                        await onChange(event, value);

                        if (row.type === 'application_type') {
                            await setApplicationType(value);
                            await setBigRockRoleIdsUnsaved(wh, bigRockRoleIdsUnsaved, bigRocksData, value);
                        }
                    };

                    return (
                        <DefaultTableInput
                            type={'select'}
                            name={`select_option::${row?.feature}`}
                            className={styles.Input}
                            onChange={onChangeValue}
                            options={options}
                            defaultValue={initialValue ?? ``}
                            ariaLabel={t('SELECT_SETTINGS_FOR_X', {
                                name: row.feature
                            })}
                        />
                    )
                }
            },
            {
                header: t('DESCRIPTION'),
                size: 3,
                render: row => {
                    return (
                        <>{row?.description}</>
                    )
                }
            }
        ];

        return <DefaultListTable
            columns={columns}
            data={
                applicationOptions?.filter(it => !it.onlyFor || it.onlyFor === applicationType)
            }
        />;
    }

    function getToggleInitialValue(bigRock: RxBigRock) {
        return bigRockRoleIdsUnsaved.includes(bigRock.role_off ?? '')
            ? 'disabled'
            : bigRockRoleIdsUnsaved.includes(bigRock.role_on ?? '')
                ? 'enabled'
                : 'default';
    }

    function getMainFeatureToggle(bigRock: RxBigRock) {
        let toggleOptions: ToggleOptionDescription[] = [
            {
                value: 'default',
                label: t('AUTO')
            }
        ];

        if (bigRock.role_on) {
            toggleOptions.unshift({
                value: 'enabled',
                label: t('ENABLED'),
                color: 'green'
            })
        }

        if (bigRock.role_off) {
            toggleOptions.push({
                value: 'disabled',
                label: t('DISABLED'),
                color: 'red'
            })
        }

        const toggleInitialValue = getToggleInitialValue(bigRock);

        return <DefaultToggle
            className={styles.Toggle}
            ariaLabel={bigRock.name}
            name={`BIG_ROCK_${bigRock.name}_TOGGLE`}
            options={toggleOptions}
            initialValue={toggleInitialValue}
            onChange={async (value) => {
                const roleOn = bigRock.role_on ?? '';
                const roleOff = bigRock.role_off ?? '';

                const addRoles = [
                    value === 'enabled' ? roleOn : '',
                    value === 'disabled' ? roleOff : ''
                ].filter(isTruthyString);

                const removeRoles = [
                    value !== 'enabled' ? roleOn : '',
                    value !== 'disabled' ? roleOff : ''
                ].filter(isTruthyString);

                const bigRockRoleIdsUnsavedNew = bigRockRoleIdsUnsaved
                    .filter(it => !removeRoles.includes(it))
                    .concat(addRoles)

                await setBigRockRoleIdsUnsaved(wh, bigRockRoleIdsUnsavedNew, bigRocksData, applicationType);
            }}
        ></DefaultToggle>;
    }

    function prepareBigRockDrawer(bigRock: RxBigRock, wh: DefaultWizardHelpers): JSX.Element {
        const subRocks = bigRocksData.filter(it => bigRock.sub_rock_ids?.includes(it.id));
        const isOn = getToggleInitialValue(bigRock) === 'enabled';
        const isTogglesRock = (bigRock.is_sub_rock ?? false) && bigRock.role_off === undefined;

        const toggle = isTogglesRock
            ? <></>
            : <span style={{float: 'right'}}
                    onClick={e => e.stopPropagation()}
                    onKeyDown={e => e.stopPropagation()}
                    onKeyUp={e => e.stopPropagation()}
            >
                    {getMainFeatureToggle(bigRock)}
            </span>;

        return (<Drawer
            key={KEY(bigRock)}
            title={<>
                <span style={{float: 'left'}}>
                    {t(bigRock.name ?? 'BIG_ROCK')}
                </span>
                {toggle}
            </>}
            //FIXME: validator marks <h2> in <button> as error. After Jira UXSPR-53 resolved, fix code
            //@ts-ignore
            headingLevel={"span"}
            className={cssClasses(styles.DrawerHeader, 'qa_' + bigRock.name.replaceAll(" ", "_"))}
            expanded={true}
        >
            <BigRockBody
                bigRock={bigRock}
                groupId={id ? id : ''}
                wh={wh}
                isOn={(isOn || isTogglesRock)}
            />
            {
                (isOn && subRocks.length > 0)
                    ?
                    <Accordion>
                        {subRocks.map(subRock => {
                            return prepareBigRockDrawer(subRock, wh)
                        })}
                    </Accordion>
                    : <></>
            }
        </Drawer>);
    }

    return (
        <div className={cssClasses(
            styles.Accordion,
            `qa-accordion`
        )}>
            <Accordion key={KEY('APPLICATION_ACCORDION' + applicationType)}>
                <Drawer
                    key={KEY('APPLICATION_DRAWER')}
                    //FIXME: validator marks <h2> in <button> as error. After Jira UXSPR-53 resolved, fix code
                    //@ts-ignore
                    headingLevel={"span"}
                    title={t('APPLICATION')}
                    expanded={true}
                    className={styles.DrawerHeader}
                >
                    {applicationDrawerTable}
                </Drawer>
                {
                    getApplicableBigRocks(bigRocksData, applicationType ?? '')
                        .filter(it => !it.is_sub_rock)
                        .map(bigRock => {
                            return prepareBigRockDrawer(bigRock, wh);
                        })
                }
            </Accordion>
        </div>
    );
}

function isApplicableBigRockRoleId(bigRocks: RxBigRock[], applicationType: string): (it: string) => boolean {
    const applicableBigRockRoleIds = getApplicableBigRockRoleIds(bigRocks, applicationType);
    return it => applicableBigRockRoleIds.includes(it);
}

function getApplicableBigRockRoleIds(bigRocks: RxBigRock[], applicationType: string): string[] {
    return getApplicableBigRocks(bigRocks, applicationType)
        .flatMap(it => [it.role_on, it.role_off])
        .filter(isTruthyString)
}

function getApplicableBigRocks(bigRocks: RxBigRock[], applicationType: string): RxBigRock[] {
    return bigRocks.filter(it => {
        const fits = !it.destiny?.length
            || it.destiny?.includes(applicationType?.toUpperCase() ?? '');

        return applicationType && fits
    });
}

function getBigRockRoleIdsSaved(bigRocksData: RxBigRock[], groupId: string = ''): string[] {
    if (!bigRocksData || bigRocksData.length === 0) {
        return [];
    }

    return bigRocksData
        .flatMap(it => {
            let bigRockRoleIdsSaved: (string | undefined)[] = [];

            if (it.enabled_group_ids?.includes(groupId)) {
                bigRockRoleIdsSaved.push(it.role_on);
            } else if (it.disabled_group_ids?.includes(groupId)) {
                bigRockRoleIdsSaved.push(it.role_off);
            } else {
                bigRockRoleIdsSaved.push('');
            }

            if (it.sub_rock_ids && it.sub_rock_ids.length > 0) {
                bigRockRoleIdsSaved = bigRockRoleIdsSaved.concat(getBigRockRoleIdsSaved(bigRocksData
                    .filter(subRock => it.sub_rock_ids?.includes(subRock.id)), it.group_rock_role));
            }
            return bigRockRoleIdsSaved;
        })
        .filter(isTruthyString);
}

function getBigRockRoleIdsUnsaved(
    wh: DefaultWizardHelpers,
    bigRockRoleIdsSaved: string[],
    bigRocksData: RxBigRock[],
    applicationType: string = ''
) {
    if (isArrayOf(isString)(wh.stepData?.bigRockRoleIdsUnsaved)) {
        return wh.stepData!.bigRockRoleIdsUnsaved;
    } else {
        setBigRockRoleIdsUnsaved(wh, bigRockRoleIdsSaved, bigRocksData, applicationType).andWeAreDone()
        return bigRockRoleIdsSaved;
    }
}

async function setBigRockRoleIdsUnsaved(
    wh: DefaultWizardHelpers,
    bigRockRoleIdsUnsavedNew: string[],
    bigRocksData: RxBigRock[],
    applicationType: string = ''
) {
    await wh.setStepMember(['bigRockRoleIdsUnsaved'], bigRockRoleIdsUnsavedNew);
    await wh.setStepMember(['bigRockRoleIdsApplicable'], bigRockRoleIdsUnsavedNew
        .filter(isApplicableBigRockRoleId(bigRocksData, applicationType ?? '')));
}
